/**
 * Transaction drill-down: the GL rows behind any number on the page, with an in-modal text search
 * and month filter. Loads only the departments it needs (one, or all for company-wide views).
 */
import { type ChangeEvent, useCallback, useMemo, useState } from 'react'

import { ICONS } from '@/components/icons/icons'
import { Modal } from '@/components/ui/Modal'
import { EmptyState, ErrorState, Skeleton } from '@/components/ui/StateMessage'
import { formatCount, formatMoney, pluralize } from '@/lib/format'

import { useTransactions } from '../hooks/useTransactions'
import { labelWithAsOf, monthNumber } from '../lib/calendar'
import { applyFilter, type DrillRequest, refine, sumAmounts } from '../lib/filterTxns'
import { useRunwayModel } from '../RunwayContext'
import { MonthChips, type MonthOption } from './MonthChips'
import styles from './TxnDrillDown.module.css'
import { TxnTable } from './TxnTable'

/** Rendering thousands of rows freezes the dialog; past this, ask the user to narrow down. */
export const ROW_CAP = 500

interface TxnDrillDownProps {
  request: DrillRequest
  onClose: () => void
}

export function TxnDrillDown({ request, onClose }: TxnDrillDownProps) {
  const { runway, calendar, deptName } = useRunwayModel()
  const codes = useMemo(
    () => (request.dept ? [request.dept] : runway.department_order),
    [request.dept, runway.department_order],
  )
  const { rows, isLoading, isError, failedCodes, retry } = useTransactions(codes)
  const [query, setQuery] = useState(request.query ?? '')
  const [month, setMonth] = useState<number | null>(null)

  const base = useMemo(() => applyFilter(rows, request.filter), [rows, request.filter])
  const visible = useMemo(() => refine(base, { query, month }), [base, query, month])
  const total = useMemo(() => sumAmounts(visible), [visible])

  const monthOptions = useMemo<MonthOption[]>(
    () => [
      { label: 'All', value: null },
      ...runway.quarter_months.map((label, i) => ({
        label,
        value: monthNumber(calendar, label),
        disabled: runway.departments[codes[0] ?? '']?.monthly[i]?.upcoming ?? false,
      })),
    ],
    [runway, calendar, codes],
  )

  const handleQuery = useCallback((event: ChangeEvent<HTMLInputElement>) => setQuery(event.target.value), [])

  const title = `${request.dept ? deptName(request.dept) : 'All Departments'} — ${request.label}`
  const subtitle = `${labelWithAsOf(runway)} · underlying GL transactions`
  const shown = visible.slice(0, ROW_CAP)

  let body
  if (isError) body = <ErrorState>Could not load transactions. Try again in a moment.</ErrorState>
  else if (isLoading) body = <Skeleton label="Loading transactions" tall />
  else if (visible.length === 0) body = <EmptyState>No transactions match this filter.</EmptyState>
  else body = <TxnTable rows={shown} showDept={!request.dept} deptName={deptName} />

  return (
    <Modal title={title} subtitle={subtitle} onClose={onClose}>
      <div className={styles.summary}>
        <div aria-live="polite">
          <b className="num">{formatCount(visible.length)}</b> {pluralize(visible.length, 'transaction')}
        </div>
        <div>
          Total: <b className="num">{formatMoney(total)}</b>
        </div>
        <div className={styles.controls}>
          <label className={styles.search}>
            <span className="visually-hidden">Search these transactions</span>
            <ICONS.search className={styles.searchIcon} size={13} />
            <input
              className={styles.searchInput}
              type="search"
              value={query}
              onChange={handleQuery}
              placeholder="Filter by vendor, memo, account…"
              autoComplete="off"
              data-autofocus={request.query ? 'true' : undefined}
            />
          </label>
          <MonthChips options={monthOptions} value={month} onChange={setMonth} />
        </div>
      </div>
      {failedCodes.length > 0 && !isError ? (
        <div className={styles.cap} role="status">
          {failedCodes.map(deptName).join(', ')} could not be loaded: the rows and total above exclude{' '}
          {failedCodes.length === 1 ? 'it' : 'them'}.{' '}
          <button type="button" className={styles.retry} onClick={retry}>
            Retry
          </button>
        </div>
      ) : null}
      <div className={styles.body}>{body}</div>
      {visible.length > ROW_CAP ? (
        <div className={styles.cap}>
          Showing the first {formatCount(ROW_CAP)} of {formatCount(visible.length)} rows — refine the search to see the
          rest. Totals include every row.
        </div>
      ) : null}
    </Modal>
  )
}
